import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { addHours, differenceInDays, differenceInMonths, parseISO, set, startOfHour } from 'date-fns';
import { StoresService } from '../../core/services/stores.service';
import { Subscription, filter, first } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MerchantsService } from '../../core/services/merchants.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '../../core/services/analytics.service';
import { PaymentFlashCampaignModalComponent } from '../../shared/modals/payment-flash-campaign-modal/payment-flash-campaign-modal.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { IntlService } from '../../core/services/intl.service';
import { NgStyle, NgClass, NgTemplateOutlet } from '@angular/common';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';

export enum SetupGuideStepNames {
  'CreateAccount' = 'createAccount',
  'ConfigureBusiness' = 'configureBusiness',
  'AddYourSchedule' = 'addYourSchedule',
  'AddYourServices' = 'addYourServices',
  'CreateFirstAppointment' = 'createFirstAppointment',
  'SetupBookingPage' = 'setupBookingPage',
  'ImportClients' = 'importClients',
}
export interface SetupGuideStep {
  id: SetupGuideStepNames;
  title: string;
  completed: boolean;
  extraInfo?: string;
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-setup-guide',
    templateUrl: './setup-guide.component.html',
    styleUrls: ['./setup-guide.component.scss'],
    imports: [ClickOutsideDirective, NgStyle, NgClass, NgTemplateOutlet, TranslatePipe],
    standalone: true
})
export class SetupGuideComponent implements OnInit {
  @Input() public storeSlug: string;
  @ViewChild('completeModalTemplate') completeModalTemplate: TemplateRef<any>;
  public modalRef: BsModalRef;
  public visible = false;
  public complete = false;
  public openSetupGuide = false;

  public currentPage = 0;

  public merchantSubscription: Subscription;

  public merchant: any;

  public setupSteps: SetupGuideStep[];

  public extraSteps: SetupGuideStep[];
  public bsModalRef: BsModalRef;
  public shouldShowFlashSale = false;

  public openTourObservableSubscription: Subscription;

  constructor(
    private router: Router,
    private storeService: StoresService,
    public merchantsService: MerchantsService,
    private translate: TranslateService,
    private modalService: BsModalService,
    private analyticsService: AnalyticsService,
    private intlService: IntlService
  ) {}

  ngOnInit(): void {
    this.merchantSubscription = this.merchantsService.activeMerchant.subscribe((res) => {
      this.merchant = res;
      const accountOlderThanTwoMonths = this.merchant?.createdAt
        ? differenceInMonths(new Date(), parseISO(this.merchant.createdAt)) > 2
        : false;
      const createFirstAppointmentDone = !!this.merchant?.settings?.showSetupGuide?.createFirstAppointment;
      const shouldHideGuideForMatureAccount = accountOlderThanTwoMonths && createFirstAppointmentDone;

      if (this.merchant?.settings?.showSetupGuide?.incomplete && !shouldHideGuideForMatureAccount) {
        setTimeout(() => {
          this.initializeOptions();
        }, 10);
      } else if (this.merchant?.settings?.showSetupGuide?.incomplete === false || shouldHideGuideForMatureAccount) {
        this.merchantSubscription?.unsubscribe();
      }

      const days = differenceInDays(new Date(), parseISO(this.merchant?.createdAt))
      const isReferral = this.merchant.referrerCode;
      this.shouldShowFlashSale = days <= 2 && !isReferral;
    });
  }

  initializeOptions() {
    this.setupSteps = [
      {
        id: SetupGuideStepNames.CreateAccount,
        title: this.translate.instant('SETUP_GUIDE.create_account'),
        completed: true,
        extraInfo: null,
      },
      {
        id: SetupGuideStepNames.ConfigureBusiness,
        title: this.translate.instant('SETUP_GUIDE.configure_business'),
        completed: true,
        extraInfo: null,
      },
      {
        id: SetupGuideStepNames.AddYourSchedule,
        title: this.translate.instant('SETUP_GUIDE.add_your_schedule'),
        completed: this.merchant?.settings?.showSetupGuide?.addYourSchedule ? true : false,
        extraInfo: null,
      },
      {
        id: SetupGuideStepNames.AddYourServices,
        title: this.translate.instant('SETUP_GUIDE.add_your_services'),
        completed: this.merchant?.settings?.showSetupGuide?.addYourServices ? true : false,
        extraInfo: null,
      },
      {
        id: SetupGuideStepNames.CreateFirstAppointment,
        title: this.translate.instant('SETUP_GUIDE.create_first_appointment'),
        completed: this.merchant?.settings?.showSetupGuide?.createFirstAppointment ? true : false,
        extraInfo: null,
      },
      {
          id: SetupGuideStepNames.ImportClients,
          title: this.translate.instant('SETUP_GUIDE.import_clients'),
          completed: this.merchant?.settings?.showSetupGuide?.importClients ? true : false,
          extraInfo: this.translate.instant('SETUP_GUIDE.win_10_eur'),
        },
    ];

    // this.extraSteps = [
    //   {
    //     id: SetupGuideStepNames.SetupBookingPage,
    //     title: this.translate.instant('SETUP_GUIDE.setup_booking_page'),
    //     completed: this.merchant?.settings?.showSetupGuide?.setupBookingPage ? true : false,
    //     extraInfo: null,
    //   },
    //   {
    //     id: SetupGuideStepNames.ImportClients,
    //     title: this.translate.instant('SETUP_GUIDE.import_clients'),
    //     completed: this.merchant?.settings?.showSetupGuide?.importClients ? true : false,
    //     extraInfo: this.translate.instant('SETUP_GUIDE.win_10_eur'),
    //   },
    // ];

    if (this.setupPercentageComplete < 100) {
      this.currentPage = 0;
    // } else if (this.extraStepsPercentageComplete < 100) {
    //   this.currentPage = 1;

      this.openTourObservableSubscription = this.merchantsService.openProductTourMenu.subscribe(() => {
        if (this.modalService.getModalsCount() > 0) {
          return;
        }
        this.openSetupGuide = true;
      });
    } else {
      this.setTourAsCompleted();
    }

    this.visible = true;
  }

  get setupPercentageComplete() {
    const completedSteps = this.setupSteps.filter((step) => step.completed);
    const percentage = Math.round((completedSteps.length / this.setupSteps.length) * 100);
    return percentage;
  }
  get extraStepsPercentageComplete() {
    const completedSteps = this.extraSteps.filter((step) => step.completed);
    const percentage = Math.round((completedSteps.length / this.extraSteps.length) * 100);
    return percentage;
  }

  get completedSetupStepsLength() {
    return this.setupSteps.filter((step) => step.completed).length;
  }

  get remainingExtraStepsLength() {
    return this.extraSteps.filter((step) => !step.completed).length;
  }

  toggleSetupGuide() {
    this.openSetupGuide = !this.openSetupGuide;
    if (this.openSetupGuide) {
      this.analyticsService.trackProductAnalyticsEvent('setup_tour_open', {});
    }
  }

  clickStep(stepId: SetupGuideStepNames) {
    const stepIndex = this.setupSteps.findIndex((s) => s.id === stepId);
    switch (stepId) {
      case SetupGuideStepNames.AddYourSchedule:
        this.router.navigateByUrl(this.storeSlug + '/dashboard/schedule');
        this.setStepCompleted(stepId);
        break;
      case SetupGuideStepNames.AddYourServices:
        this.router.navigateByUrl(this.storeSlug + '/dashboard/services');
        this.setStepCompleted(stepId);
        break;
      case SetupGuideStepNames.CreateFirstAppointment:
        this.createNewBooking();
        break;
      case SetupGuideStepNames.SetupBookingPage:
        this.router.navigateByUrl(this.storeSlug + '/editor');
        this.setStepCompleted(stepId);
        break;
      case SetupGuideStepNames.ImportClients:
        this.router.navigateByUrl(this.storeSlug + '/dashboard/clients');
        this.setStepCompleted(stepId);
        break;
    }
    this.openSetupGuide = false;
  }

  createNewBooking() {
    this.router.navigateByUrl(this.storeSlug + '/dashboard');
    const date = startOfHour(addHours(new Date(), 1));
    this.merchantsService.doSetupFirstBooking.next(date);
  }

  setStepCompleted(stepId: SetupGuideStepNames) {
    const settings = { ...this.merchant.settings };
    if (!settings.showSetupGuide) {
      settings.showSetupGuide = {};
    }
    settings.showSetupGuide[stepId] = true;
    this.merchantsService.updateMerchant(this.merchant.id, { settings }).subscribe((res) => {
      this.analyticsService.trackProductAnalyticsEvent('setup_tour_step_complete', {stepId});
      this.merchantsService.activeMerchant.next(res);
    });
  }

  setTourAsCompleted() {
    const settings = { ...this.merchant.settings };
    settings.showSetupGuide.incomplete = false;
    this.merchantsService.updateMerchant(this.merchant.id, { settings }).subscribe((res) => {
      this.analyticsService.trackProductAnalyticsEvent('setup_tour_complete', {});
      this.merchantsService.activeMerchant.next(res);
      setTimeout(() => {
        this.showCompleteModal();
      }, 2000);
    });
  }

  showCompleteModal() {
    if (this.modalRef) {
      return;
    }
    this.modalRef = this.modalService.show(this.completeModalTemplate);
    this.modalRef.onHide.subscribe(() => {
      this.hideTour();
    });
  }

  hideTour() {
    this.complete = true;
      setTimeout(() => {
        this.visible = false;
      }, 4000);
  }

  showFlashPromo() {
    this.bsModalRef = this.modalService.show(PaymentFlashCampaignModalComponent, { class: 'modal-md' });
    this.toggleSetupGuide();
  }

  tourFinish() {
    this.router.navigateByUrl(this.storeSlug + '/editor');
    this.modalRef.hide();
  }

}
